// Front-end undo/redo: a lightweight per-(session × object) store that drives
// the Undo affordance, backed by the server which is the source of truth.
//
// State updates come from three places, by design:
//  - hot path (mutation success): OPTIMISTIC, synchronous, no network — the
//    button must appear instantly without an extra round-trip per edit
//    (markUndoable);
//  - navigation (entering a view/object) and first mount: an authoritative
//    GET /history?limit=1 probe reconciles the truth (refreshUndoState);
//  - after an undo/redo: the endpoint response already carries the fresh state.
// There is no polling.

import i18n from "i18next";
import { toast } from "sonner";
import { create } from "zustand";
import { translate } from "../i18n/i18n";
import { BASE_URL } from "./constants";
import { getUndoSessionId } from "./session";
import { get, post } from "./utils";

export interface SkippedChange {
    objectKey: string;
    recordKey: string;
    fieldKey?: string;
    reason: string;
}

// nextUndoLabel / nextRedoLabel are i18n CODES (e.g. "compozz.data.undo.delete.1"),
// not text; nextUndoCount / nextRedoCount fill their {{p0}} parameter.
export interface UndoState {
    canUndo: boolean;
    canRedo: boolean;
    nextUndoLabel?: string;
    nextUndoCount?: number;
    nextRedoLabel?: string;
    nextRedoCount?: number;
}

export interface UndoCell {
    objectKey: string;
    recordKey: string;
    fieldKey: string;
    value: any;
}

export interface UndoResult extends UndoState {
    operationId: string;
    // i18n code + count of the operation that was undone/redone (one user action).
    label?: string;
    count?: number;
    redoToken?: string;
    applied: number;
    skipped?: SkippedChange[];
    objectKey?: string;
    // structural = rows added/removed (create/delete/duplicate/import) → full refetch.
    structural?: boolean;
    // cells = applied cell values for an update/paste → optimistic grid update.
    cells?: UndoCell[];
}

// Undo label i18n codes — must match compozz-data/i18n/i18n.go and the locale keys.
export const UndoLabelCode = {
    update: "compozz.data.undo.update.1",
    delete: "compozz.data.undo.delete.1",
    create: "compozz.data.undo.create.1",
    duplicate: "compozz.data.undo.duplicate.1",
    import: "compozz.data.undo.import.1",
    link: "compozz.data.undo.link.0",
    unlink: "compozz.data.undo.unlink.0",
} as const;

// trUndo resolves an undo i18n code (+ optional count) using the global i18n
// instance, so it works outside React components (toasts/tooltips, where no
// `t` is available). Thin adapter over translate() to keep the placeholder /
// pluralization handling in one place.
function trUndo(code?: string, count?: number): string {
    if (!code) {
        return "";
    }
    return translate(i18n.t, code, count !== undefined ? [count] : []);
}

type UndoStoreState = {
    // Stack availability keyed by objectKey (the scope of a stack within a session).
    byObject: Record<string, UndoState>;
    setState: (objectKey: string, state: UndoState) => void;
    // markUndoable optimistically flags an object's stack as undoable right after
    // a successful mutation — synchronous, no network. A new mutation also clears
    // the redo branch (server truncates it), hence canRedo:false. labelCode is an
    // i18n code; count fills its parameter.
    markUndoable: (objectKey: string, labelCode: string, count?: number) => void;
};

export const useUndoStore = create<UndoStoreState>((set) => ({
    byObject: {},
    setState: (objectKey, state) =>
        set((s) => ({ byObject: { ...s.byObject, [objectKey]: state } })),
    markUndoable: (objectKey, labelCode, count) =>
        set((s) => ({
            byObject: {
                ...s.byObject,
                [objectKey]: { canUndo: true, canRedo: false, nextUndoLabel: labelCode, nextUndoCount: count },
            },
        })),
}));

// formatUndoLabel translates the "next undo" label code stored for an object
// (used by the button tooltip).
export function formatNextUndoLabel(state: UndoState | undefined): string {
    return trUndo(state?.nextUndoLabel, state?.nextUndoCount);
}

// refreshUndoState re-fetches the authoritative stack state and updates the
// store. Used as the reconciliation point on navigation (entering a view/object)
// and on first mount — NOT on the hot mutation path (that one is optimistic).
export async function refreshUndoState(workspaceKey: string, objectKey: string): Promise<void> {
    if (!workspaceKey || !objectKey) {
        return;
    }
    const state = await fetchUndoState(workspaceKey, objectKey);
    if (state) {
        useUndoStore.getState().setState(objectKey, state);
    }
}

export async function fetchUndoState(workspaceKey: string, objectKey: string): Promise<UndoState | null> {
    const session = getUndoSessionId();
    const url = `${BASE_URL}/data/records/history?workspace=${encodeURIComponent(workspaceKey)}&object=${encodeURIComponent(objectKey)}&session=${encodeURIComponent(session)}&limit=1`;
    const res = await get<UndoState>(url);
    if (!res || (res as any).i18n || (res as any).statusCode) {
        return null;
    }
    return res as UndoState;
}

function applyResult(objectKey: string, result: UndoResult) {
    useUndoStore.getState().setState(objectKey, {
        canUndo: result.canUndo,
        canRedo: result.canRedo,
        nextUndoLabel: result.nextUndoLabel,
        nextUndoCount: result.nextUndoCount,
        nextRedoLabel: result.nextRedoLabel,
        nextRedoCount: result.nextRedoCount,
    });
}

async function call(endpoint: "undo" | "redo", workspaceKey: string, objectKey: string): Promise<UndoResult | null> {
    const body = {
        workspaceKey,
        objectKey,
        sessionId: getUndoSessionId(),
        operationId: "last",
    };
    const res = await post<UndoResult>(`${BASE_URL}/data/records/${endpoint}`, body);
    if (!res || (res as any).i18n) {
        return null;
    }
    return res as UndoResult;
}

// performUndo applies the top undo of the current object stack, updates the
// store, surfaces conflicts via a toast and refreshes the grid. It resolves
// only once the (possibly async) refresh callback has completed, so callers
// can keep a spinner up until the rows are actually refreshed.
export async function performUndo(workspaceKey: string, objectKey: string, refresh: (result: UndoResult) => void | Promise<void>): Promise<void> {
    const result = await call("undo", workspaceKey, objectKey);
    await handleResult(objectKey, result, refresh, false);
}

export async function performRedo(workspaceKey: string, objectKey: string, refresh: (result: UndoResult) => void | Promise<void>): Promise<void> {
    const result = await call("redo", workspaceKey, objectKey);
    await handleResult(objectKey, result, refresh, true);
}

async function handleResult(objectKey: string, result: UndoResult | null, refresh: (result: UndoResult) => void | Promise<void>, isRedo: boolean) {
    if (!result) {
        return;
    }
    applyResult(objectKey, result);
    await refresh(result);

    const skipped = result.skipped?.length ?? 0;
    if (skipped > 0) {
        // Partial conflict: a few cells were changed concurrently and skipped.
        toast.warning(i18n.t("undo.conflict", { p0: result.applied, p1: skipped }) as string);
        return;
    }
    // Full success on a non-trivial action: a discreet confirmation showing the
    // operation's translated label (one user action, e.g. "modification de
    // lien(s)") rather than the count of low-level compensating changes.
    // Single-cell undos stay silent to avoid toast spam on repeated Ctrl-Z.
    if (result.applied > 1) {
        const prefix = i18n.t(isRedo ? "redo.done" : "undo.done") as string;
        const label = trUndo(result.label, result.count);
        toast.success(label ? `${prefix} : ${label}` : prefix);
    }
}
